import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Add,
  Cancel,
  Send,
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Visibility,
  Edit,
  Delete,
  Image,
  VideoFile,
  AudioFile,
  Description,
  AttachFile,
  ErrorOutline,
  Fullscreen,
  FullscreenExit
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { API_BASE_URL } from 'envConfig';
import { Logger } from 'utils/Logger';

type MediaAttachment = {
  type: 'image' | 'video' | 'document' | 'audio';
  filename: string;
  mimeType: string;
  size: number;
  data?: string;
  localUrl?: string;
  originalName?: string;
};

type NotificationForm = {
  destinationNumber: string;
  messageBody: string;
  mediaAttachments: MediaAttachment[];
};

export const PushNotifications: React.FC = () => {
  const user = useSelector((state: RootState) => state.authentication.user);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<NotificationForm>({
    destinationNumber: '',
    messageBody: '',
    mediaAttachments: []
  });
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const [destinationError, setDestinationError] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const openDialog = () => {
    setForm({
      destinationNumber: '',
      messageBody: '',
      mediaAttachments: []
    });
    setDestinationError('');
    setUploadError('');
    setShowPreview(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    // Cleanup blob URLs
    form.mediaAttachments.forEach(att => {
      if (att.localUrl) {
        URL.revokeObjectURL(att.localUrl);
      }
    });
    setDialogOpen(false);
  };

  // Validate destination number - must start with + followed by country code
  const validateDestinationNumber = (value: string) => {
    if (!value) {
      setDestinationError('Destination number is required');
      return false;
    }
    if (!value.startsWith('+')) {
      setDestinationError('Number must start with + followed by country code (e.g., +1, +44, +91)');
      return false;
    }
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    if (!phoneRegex.test(value)) {
      setDestinationError('Invalid phone number format. Use format: +[country code][number]');
      return false;
    }
    setDestinationError('');
    return true;
  };

  const handleDestinationChange = (value: string) => {
    setForm(prev => ({ ...prev, destinationNumber: value }));
    if (value) {
      validateDestinationNumber(value);
    } else {
      setDestinationError('');
    }
  };

  // Apply formatting to selected text
  const applyFormatting = (format: 'bold' | 'italic' | 'strikethrough') => {
    if (!selection || selection.start === selection.end) return;
    const before = form.messageBody.substring(0, selection.start);
    const selected = form.messageBody.substring(selection.start, selection.end);
    const after = form.messageBody.substring(selection.end);
    const formatted = format === 'bold' ? `*${selected}*` : format === 'italic' ? `_${selected}_` : `~${selected}~`;
    const newText = before + formatted + after;
    setForm(prev => ({ ...prev, messageBody: newText }));
  };

  // Format message for preview (WhatsApp style)
  const formatWhatsAppMessage = (text: string) => {
    return (text || '')
      .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      .replace(/~([^~]+)~/g, '<del>$1</del>')
      .replace(/```([^`]+)```/g, '<code>$1</code>')
      .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
      .replace(/\n/g, '<br>');
  };

  // Handle media upload
  const handleMediaUpload = async (file: File) => {
    try {
      setUploadingMedia(true);
      setUploadError('');
      
      // Check file size (16MB limit)
      const maxSize = 16 * 1024 * 1024;
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
        throw new Error(`File size (${sizeInMB}MB) exceeds the maximum allowed size of 16MB.`);
      }

      // Determine media type
      let mediaType: 'image' | 'video' | 'document' | 'audio' = 'document';
      if (file.type.startsWith('image/')) mediaType = 'image';
      else if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('audio/')) mediaType = 'audio';

      // Create local URL for preview
      const localUrl = URL.createObjectURL(file);
      
      // Read file as base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const newAttachment: MediaAttachment = {
          type: mediaType,
          filename: file.name,
          originalName: file.name,
          mimeType: file.type,
          size: file.size,
          data: base64,
          localUrl
        };

        setForm(prev => ({
          ...prev,
          mediaAttachments: [...prev.mediaAttachments, newAttachment]
        }));
      };
      reader.readAsDataURL(file);

    } catch (error) {
      Logger.error('PushNotifications', `Media upload failed: ${error}`);
      setUploadError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setUploadingMedia(false);
    }
  };

  // Handle media delete
  const handleMediaDelete = (filename: string) => {
    const attachment = form.mediaAttachments.find(att => att.filename === filename);
    if (attachment?.localUrl) {
      URL.revokeObjectURL(attachment.localUrl);
    }
    setForm(prev => ({
      ...prev,
      mediaAttachments: prev.mediaAttachments.filter(att => att.filename !== filename)
    }));
  };

  // Get media URL for display
  const getMediaUrl = (attachment: MediaAttachment): string => {
    if (attachment.localUrl) return attachment.localUrl;
    if (attachment.data) return `data:${attachment.mimeType};base64,${attachment.data}`;
    return '';
  };

  // Handle send notification
  const handleSend = async () => {
    if (!validateDestinationNumber(form.destinationNumber)) return;
    if (!form.messageBody.trim()) return;

    try {
      setSending(true);
      // TODO: Implement actual API call to send notification
      Logger.debug('PushNotifications', `Sending notification to ${form.destinationNumber}`);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      closeDialog();
    } catch (error) {
      Logger.error('PushNotifications', `Failed to send notification: ${error}`);
    } finally {
      setSending(false);
    }
  };

  const isSendDisabled = () => {
    return !form.destinationNumber || 
           !form.messageBody.trim() || 
           !!destinationError || 
           sending;
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      {/* Title Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4">Push Notifications Records</Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<Add />}
          onClick={openDialog}
          sx={{
            background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #22c55e 0%, #0d9488 100%)',
            }
          }}
        >
          SEND NEW NOTIFICATION
        </Button>
      </Box>

      {/* Placeholder for records table */}
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No push notification records found.
        </Typography>
      </Paper>

      {/* Send Notification Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog} 
        fullWidth 
        maxWidth={isMaximized ? false : "lg"}
        fullScreen={isMaximized}
        keepMounted={false}
        TransitionProps={{ unmountOnExit: true }}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMaximized ? 0 : 'auto',
            maxWidth: isMaximized ? '100vw' : 'lg',
            maxHeight: isMaximized ? '100vh' : '90vh',
            width: isMaximized ? '100vw' : '100%',
            height: isMaximized ? '100vh' : 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
          color: 'white',
          pr: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Send />
            <Typography variant="h6" component="span">Send New Notification</Typography>
          </Box>
          <IconButton
            onClick={() => setIsMaximized(!isMaximized)}
            size="small"
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isMaximized ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          pt: 3,
          height: isMaximized ? 'calc(100vh - 140px)' : 'auto',
          overflow: 'auto'
        }}>
          {/* WhatsApp Formatting Guide */}
          <Paper sx={{ 
            p: 2, 
            mb: 3,
            mt: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`
          }}>
            <Typography variant="subtitle2" gutterBottom>
              WhatsApp Formatting Guide
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the formatting buttons to style your message:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, mt: 1 }}>
              <Typography component="li" variant="body2" color="text.secondary">*Bold text* - Use the <strong>B</strong> button</Typography>
              <Typography component="li" variant="body2" color="text.secondary">_Italic text_ - Use the <em>I</em> button</Typography>
              <Typography component="li" variant="body2" color="text.secondary">~Strikethrough text~ - Use the <del>S</del> button</Typography>
            </Box>
          </Paper>

          {/* Destination Number */}
          <TextField
            label="Destination Number"
            value={form.destinationNumber}
            onChange={(e) => handleDestinationChange(e.target.value)}
            fullWidth
            required
            margin="normal"
            placeholder="+1234567890"
            error={!!destinationError}
            helperText={destinationError}
            InputProps={{
              startAdornment: (
                <Box sx={{ color: 'text.secondary', mr: 1 }}>
                  📱
                </Box>
              )
            }}
          />
          {!destinationError && (
            <Typography variant="subtitle2" sx={{ mt: 0.5, ml: 1.75 }}>
              Enter number with country code (e.g., +1 for US, +44 for UK, +91 for India)
            </Typography>
          )}

          {/* Message Body Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Message Body *</Typography>
              <Button
                size="small"
                variant={showPreview ? "contained" : "outlined"}
                startIcon={showPreview ? <Edit /> : <Visibility />}
                onClick={() => setShowPreview(!showPreview)}
                sx={{
                  backgroundColor: showPreview ? '#25d366' : 'transparent',
                  color: showPreview ? '#ffffff' : '#25d366',
                  borderColor: '#25d366',
                  '&:hover': {
                    backgroundColor: showPreview ? '#128c7e' : 'rgba(37, 211, 102, 0.1)'
                  }
                }}
              >
                {showPreview ? 'Edit' : 'Preview'}
              </Button>
            </Box>

            {showPreview ? (
              <Box sx={{ 
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                borderRadius: '8px',
                padding: '16px',
                background: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f0f2f5',
                minHeight: '200px'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  WhatsApp Message Preview
                </Typography>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flexDirection: 'column',
                  alignItems: 'flex-end'
                }}>
                  {/* Media Attachments Preview */}
                  {form.mediaAttachments.map((attachment, index) => (
                    <Box key={index} sx={{
                      maxWidth: '70%',
                      background: '#25d366',
                      borderRadius: '18px 18px 4px 18px',
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      {attachment.mimeType?.startsWith('image/') && (
                        <img 
                          src={getMediaUrl(attachment)}
                          alt={attachment.filename}
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                      {attachment.mimeType?.startsWith('video/') && (
                        <video 
                          src={getMediaUrl(attachment)}
                          controls
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                      {attachment.mimeType?.startsWith('audio/') && (
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AudioFile sx={{ color: '#ffffff', fontSize: 24 }} />
                          <Typography variant="body2" sx={{ color: '#ffffff' }}>
                            {attachment.originalName || 'Audio File'}
                          </Typography>
                        </Box>
                      )}
                      {!attachment.mimeType?.startsWith('image/') && 
                       !attachment.mimeType?.startsWith('video/') && 
                       !attachment.mimeType?.startsWith('audio/') && (
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description sx={{ color: '#ffffff', fontSize: 24 }} />
                          <Typography variant="body2" sx={{ color: '#ffffff' }}>
                            {attachment.originalName || 'Document'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Message Bubble */}
                  <Box sx={{
                    maxWidth: '70%',
                    background: '#25d366',
                    color: '#ffffff',
                    padding: '8px 12px',
                    borderRadius: '18px 18px 4px 18px',
                    wordWrap: 'break-word',
                    '& strong': { fontWeight: 'bold' },
                    '& em': { fontStyle: 'italic' },
                    '& del': { textDecoration: 'line-through' },
                    '& code': {
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      padding: '2px 4px',
                      borderRadius: '4px'
                    },
                    '& blockquote': {
                      borderLeft: '3px solid rgba(255, 255, 255, 0.5)',
                      paddingLeft: '8px',
                      margin: '4px 0'
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(form.messageBody) || '<em>No message</em>' }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {/* Formatting Toolbar */}
                <Box sx={{ 
                  background: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
                  borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                  padding: '8px 12px',
                  display: 'flex',
                  gap: '4px'
                }}>
                  <ToggleButtonGroup size="small" sx={{ 
                    '& .MuiToggleButton-root': { 
                      border: 'none', 
                      color: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f' 
                    } 
                  }}>
                    <ToggleButton 
                      value="bold" 
                      onClick={() => applyFormatting('bold')}
                      sx={{ 
                        '&:hover': { color: '#25d366' },
                        '&.Mui-selected': { color: '#25d366', backgroundColor: 'rgba(37, 211, 102, 0.1)' }
                      }}
                    >
                      <FormatBold fontSize="small" />
                    </ToggleButton>
                    <ToggleButton 
                      value="italic" 
                      onClick={() => applyFormatting('italic')}
                      sx={{ 
                        '&:hover': { color: '#25d366' },
                        '&.Mui-selected': { color: '#25d366', backgroundColor: 'rgba(37, 211, 102, 0.1)' }
                      }}
                    >
                      <FormatItalic fontSize="small" />
                    </ToggleButton>
                    <ToggleButton 
                      value="strikethrough" 
                      onClick={() => applyFormatting('strikethrough')}
                      sx={{ 
                        '&:hover': { color: '#25d366' },
                        '&.Mui-selected': { color: '#25d366', backgroundColor: 'rgba(37, 211, 102, 0.1)' }
                      }}
                    >
                      <FormatStrikethrough fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                
                {/* Text Area */}
                <TextField
                  multiline
                  minRows={6}
                  maxRows={10}
                  value={form.messageBody}
                  onChange={(e) => setForm(prev => ({ ...prev, messageBody: e.target.value }))}
                  onSelect={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    setSelection({
                      start: target.selectionStart,
                      end: target.selectionEnd
                    });
                  }}
                  variant="standard"
                  fullWidth
                  placeholder="Type your message here..."
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      lineHeight: '1.4'
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: '150px',
                      color: (theme) => theme.palette.text.primary,
                      backgroundColor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Media Attachments Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Media Attachments
            </Typography>
            
            {/* File Upload Button */}
            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setUploadError('');
                    handleMediaUpload(file);
                  }
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
                id="push-notification-media-upload"
                disabled={uploadingMedia}
              />
              <label htmlFor="push-notification-media-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploadingMedia ? <CircularProgress size={16} /> : <AttachFile />}
                  disabled={uploadingMedia}
                  sx={{
                    borderColor: '#25d366',
                    color: '#25d366',
                    '&:hover': {
                      borderColor: '#128c7e',
                      backgroundColor: 'rgba(37, 211, 102, 0.1)'
                    }
                  }}
                >
                  {uploadingMedia ? 'Uploading...' : 'Add Media'}
                </Button>
              </label>
              
              {/* Error Message */}
              {uploadError && (
                <Box sx={{ mt: 1 }}>
                  <Typography 
                    variant="caption" 
                    color="error" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      padding: 1,
                      borderRadius: 1,
                      border: '1px solid rgba(244, 67, 54, 0.3)'
                    }}
                  >
                    <ErrorOutline sx={{ fontSize: 16 }} />
                    {uploadError}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Media Attachments List */}
            {form.mediaAttachments.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {form.mediaAttachments.map((attachment, index) => (
                  <Card key={index} sx={{ 
                    border: '1px solid #e9edef',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {attachment.mimeType?.startsWith('image/') && (
                      <CardMedia
                        component="img"
                        height="120"
                        image={getMediaUrl(attachment)}
                        alt={attachment.filename}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    
                    {attachment.mimeType?.startsWith('video/') && (
                      <Box sx={{ position: 'relative', height: 120 }}>
                        <video
                          src={getMediaUrl(attachment)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '40px',
                          height: '40px',
                          background: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Box sx={{
                            width: 0,
                            height: 0,
                            borderTop: '8px solid transparent',
                            borderBottom: '8px solid transparent',
                            borderLeft: '12px solid white',
                            marginLeft: '2px'
                          }} />
                        </Box>
                      </Box>
                    )}
                    
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {attachment.mimeType?.startsWith('image/') && <Image sx={{ color: '#25d366', fontSize: 20 }} />}
                        {attachment.mimeType?.startsWith('video/') && <VideoFile sx={{ color: '#25d366', fontSize: 20 }} />}
                        {attachment.mimeType?.startsWith('audio/') && <AudioFile sx={{ color: '#25d366', fontSize: 20 }} />}
                        {!attachment.mimeType?.startsWith('image/') && 
                         !attachment.mimeType?.startsWith('video/') && 
                         !attachment.mimeType?.startsWith('audio/') && 
                         <Description sx={{ color: '#25d366', fontSize: 20 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>
                          {attachment.originalName || 'Unknown File'}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleMediaDelete(attachment.filename)}
                          sx={{ color: '#ff4444' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {attachment.mimeType ? attachment.mimeType.split('/')[0].charAt(0).toUpperCase() + attachment.mimeType.split('/')[0].slice(1) : 'Unknown'} • {((attachment.size || 0) / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={closeDialog} 
            startIcon={<Cancel />}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSend}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
            disabled={isSendDisabled()}
            sx={{
              background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #22c55e 0%, #0d9488 100%)',
              },
              '&.Mui-disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
